import Animated, {
  Easing,
  SharedValue,
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated";
import { useRadiusSize, useTheme } from "arkh";

import React from "react";
import { WidgetPosWithSubs } from "../types";
import { graphql, useLazyLoadQuery } from "react-relay";
import { FocusFrameQuery } from "./__generated__/FocusFrameQuery.graphql";

export const FocusFrame = React.memo(FocusFrameC);
function FocusFrameC(props: {
  positions: SharedValue<WidgetPosWithSubs[]>;
  unitSize: number;
  spaceBetweenColumns: number;
  storecastId: string;
}) {
  const focus = useLazyLoadQuery<FocusFrameQuery>(
    graphql`
      query FocusFrameQuery($storecastId: ID!) {
        dashboard(id: $storecastId) {
          id
          focusedNodeId
        }
      }
    `,
    {
      storecastId: props.storecastId,
    }
  );
  const focusedWidgetId = focus.dashboard?.focusedNodeId;
  const theme = useTheme();
  const cornerRadius = useRadiusSize("large");

  const frameStyle = useAnimatedStyle(() => {
    const focusedPosItem =
      props.positions.value.find((pos) => pos.id === focusedWidgetId) ??
      props.positions.value
        .flatMap((v) => v.sub)
        .find((s) => s?.id === focusedWidgetId);
    if (!focusedPosItem) {
      return { transform: [{ scale: 0 }], display: "none" };
    }

    return {
      width: withTiming(props.unitSize * focusedPosItem.w),
      height: withTiming(props.unitSize * focusedPosItem.h),
      display: "flex",
      opacity: 1,
      borderRadius: cornerRadius,
      borderColor: theme.primary,
      borderStyle: "solid",
      borderWidth: 3,
      zIndex: 1,
      transform: [
        { scale: 1 },
        {
          translateX: withTiming(
            (focusedPosItem.c + 0.5) * props.spaceBetweenColumns +
              focusedPosItem.c * props.unitSize * 2 +
              focusedPosItem.x * props.unitSize,
            { duration: 200, easing: Easing.inOut(Easing.ease) }
          ),
        },
        {
          translateY: withTiming(focusedPosItem.y * props.unitSize, {
            duration: 200,
            easing: Easing.inOut(Easing.ease),
          }),
        },
      ],
    };
  });

  return (
    <Animated.View shouldRasterizeIOS pointerEvents="none" style={frameStyle} />
  );
}
